import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../../i18n';
import { useCurrentUser } from '../../../../../hooks/useCurrentUser';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import {
  fetchPersonaOrganigrama,
  fetchPersonaAsignaciones,
  finalizarPersonaAsignacion,
  type PersonaDetalle,
  type PersonaOrgChart,
  type AsignacionItem,
} from '../../../../shared/services/api';
import { cardStyle, formatDate } from './persona-detail-shared';
import { AssignToPuestoModal } from './AssignToPuestoModal';

export function PersonaPuestoTab({
  data,
  personaId,
  onRefresh,
}: {
  data: PersonaDetalle;
  personaId: string;
  onRefresh?: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [orgChart, setOrgChart] = useState<PersonaOrgChart | null>(null);
  const [historial, setHistorial] = useState<AsignacionItem[]>([]);
  const { isAdmin } = useCurrentUser();
  const [showAssignPuesto, setShowAssignPuesto] = useState(false);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    danger?: boolean;
    onConfirm: () => void;
  } | null>(null);

  const loadHistorial = useCallback(() => {
    fetchPersonaAsignaciones(personaId)
      .then((r) => setHistorial(r.data))
      .catch(() => {});
  }, [personaId]);

  useEffect(() => {
    fetchPersonaOrganigrama(personaId)
      .then((r) => setOrgChart(r.data))
      .catch(() => {});
    loadHistorial();
  }, [personaId, loadHistorial]);

  const handleFinalizar = (asignacionId: string) => {
    setConfirmState({
      open: true,
      title: t.common.confirmar,
      message: t.people.confirmFinalizarAsignacion,
      danger: false,
      onConfirm: async () => {
        await finalizarPersonaAsignacion(personaId, asignacionId);
        fetchPersonaAsignaciones(personaId)
          .then((r) => setHistorial(r.data))
          .catch(() => {});
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Current position */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-primario)' }}>
          Puesto Actual
        </h3>
        {data.puestoActual ? (
          <div>
            <div
              onClick={() =>
                navigate(
                  `/structure/puestos/${(data.puestoActual as NonNullable<typeof data.puestoActual>).puesto.id}`,
                )
              }
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: 'var(--color-secundario)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              {data.puestoActual.puesto.titulo}
            </div>
            <div style={{ fontSize: 12, color: '#5A6171', marginTop: 4 }}>
              {data.puestoActual.departamento?.nombre} / {data.puestoActual.organizacion?.nombre}
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
              Nivel: {data.puestoActual.puesto.nivel}
            </div>
          </div>
        ) : (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>Sin puesto asignado</div>
        )}
      </div>

      {/* Mini org chart */}
      {orgChart && orgChart.puesto && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-primario)' }}>
            Posicion en Organigrama
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            {/* Supervisor */}
            {orgChart.supervisor && (
              <>
                <div
                  style={{
                    padding: '8px 16px',
                    background: '#F1F5F9',
                    borderRadius: 8,
                    textAlign: 'center',
                    border: '1px solid #E2E8F0',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600 }}>
                    {orgChart.supervisor.persona
                      ? `${orgChart.supervisor.persona.nombre} ${orgChart.supervisor.persona.apellidos}`
                      : '(Vacante)'}
                  </div>
                  <div
                    onClick={() => navigate(`/structure/puestos/${orgChart.supervisor!.puesto.id}`)}
                    style={{ fontSize: 10, color: 'var(--color-secundario)', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                  >
                    {orgChart.supervisor.puesto.titulo}
                  </div>
                </div>
                <div style={{ width: 1, height: 16, background: '#D1D5DB' }} />
              </>
            )}

            {/* Current */}
            <div
              style={{
                padding: '10px 20px',
                background: 'var(--color-primario)',
                color: '#fff',
                borderRadius: 8,
                textAlign: 'center',
                fontWeight: 600,
              }}
            >
              <div style={{ fontSize: 13 }}>
                {orgChart.persona.nombre} {orgChart.persona.apellidos}
              </div>
              <div style={{ fontSize: 10, opacity: 0.8 }}>{orgChart.puesto.titulo}</div>
            </div>

            {/* Subordinates */}
            {orgChart.subordinados.length > 0 && (
              <>
                <div style={{ width: 1, height: 16, background: '#D1D5DB' }} />
                <div
                  style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}
                >
                  {orgChart.subordinados.map((s, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '6px 12px',
                        background: '#F8FAFC',
                        borderRadius: 6,
                        textAlign: 'center',
                        border: '1px solid #E2E8F0',
                        fontSize: 11,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        {s.persona ? `${s.persona.nombre} ${s.persona.apellidos}` : '(Vacante)'}
                      </div>
                      <div
                        onClick={() => navigate(`/structure/puestos/${s.puesto.id}`)}
                        style={{ color: 'var(--color-secundario)', cursor: 'pointer' }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        {s.puesto.titulo}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {data.puestoActual?.puesto?.id && (
            <button
              onClick={() =>
                navigate(
                  `/structure?focusPuesto=${(data.puestoActual as NonNullable<typeof data.puestoActual>).puesto.id}&tab=organigrama`,
                )
              }
              style={{
                marginTop: 12,
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                border: '1px solid var(--color-acento, #33BCE1)',
                borderRadius: 6,
                background: 'transparent',
                color: 'var(--color-acento, #33BCE1)',
                cursor: 'pointer',
              }}
            >
              {t.structure.orgChart.viewInOrgChart ?? 'Ver en organigrama'} →
            </button>
          )}
        </div>
      )}

      {/* Assign button (admin) */}
      {isAdmin && (
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => setShowAssignPuesto(true)}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'inherit',
              border: '1px solid #2F5496',
              borderRadius: 6,
              background: '#2F5496',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            + Asignar a puesto
          </button>
        </div>
      )}

      {showAssignPuesto && (
        <AssignToPuestoModal
          personaId={personaId}
          onClose={() => setShowAssignPuesto(false)}
          onAssigned={() => {
            setShowAssignPuesto(false);
            loadHistorial();
            onRefresh?.();
          }}
        />
      )}

      {/* History */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-primario)' }}>
          Historial de Puestos
        </h3>
        {historial.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 13 }}>Sin historial</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0E243E' }}>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Puesto
                </th>
                <th
                  style={{
                    textAlign: 'left',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Departamento
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Desde
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Hasta
                </th>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '14px 20px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}
                >
                  Estado
                </th>
                <th style={{ padding: '14px 20px' }}></th>
              </tr>
            </thead>
            <tbody>
              {historial.map((h) => (
                <tr key={h.id} style={{ borderBottom: '1px solid #F2F4F6' }}>
                  <td style={{ padding: '14px 20px', fontSize: 14 }}>
                    <span
                      onClick={() =>
                        (h.puestoId || h.puesto?.id) &&
                        navigate(`/structure/puestos/${h.puestoId || h.puesto?.id}`)
                      }
                      style={{ color: 'var(--color-secundario)', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                    >
                      {h.puesto?.titulo}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px', fontSize: 14 }}>
                    {h.puesto?.departamento?.nombre ?? '—'}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'center', fontSize: 14 }}>
                    {formatDate(h.fechaInicio)}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'center', fontSize: 14 }}>
                    {formatDate(h.fechaFin)}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'center' }}>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 500,
                        background: h.activo ? '#F0FDF4' : '#F3F4F6',
                        color: h.activo ? '#16A34A' : '#9CA3AF',
                      }}
                    >
                      {h.activo ? 'Activo' : 'Finalizado'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'center' }}>
                    {h.activo && (
                      <button
                        onClick={() => handleFinalizar(h.id)}
                        style={{
                          padding: '4px 12px',
                          border: '1px solid #DC2626',
                          borderRadius: 4,
                          background: 'transparent',
                          color: '#DC2626',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontFamily: 'inherit',
                        }}
                      >
                        Finalizar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmState && (
        <ConfirmDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          danger={confirmState.danger}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}
